// Draw-to-strategy (Dom reshape): the note composer for a strategy marker. Opened when a
// leg tool (Buy entry / Sell entry / Exit / Stop / Target) is clicked on the chart. The leg
// is fixed by the tool; the user completes "I'd ___ here because…" and locks it. The marker
// then lives on the chart as a drawing carrying this note (shown on hover; pushed to the AI).
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ArrowRight, X } from 'lucide-react'
import { legByKind } from './legTypes'

const MAX_W = 340 // card width (px) — wider so the note has room
const MIN_W = 220 // floor on narrow phone panes; keeps the header/price row usable

const toneClass = (t: string): string =>
  t === 'success' ? 'text-success' : t === 'danger' ? 'text-danger' : 'text-foreground'

export interface WorkbenchEntryComposerProps {
  x: number
  y: number
  paneW: number
  paneH: number
  price: string
  timeLabel: string
  defaultLeg?: string
  /** Position/trade mode passes its own lead/label/tone/glyph/placeholder; otherwise the leg's. */
  lead?: string
  label?: string
  tone?: string
  glyph?: string
  placeholder?: string
  initialValue?: string
  onSubmit: (legKind: string, reason: string) => void
  onCancel: () => void
}

export function WorkbenchEntryComposer({
  x,
  y,
  paneW,
  paneH,
  price,
  timeLabel,
  defaultLeg = 'buy',
  lead,
  label,
  tone,
  glyph,
  placeholder,
  initialValue = '',
  onSubmit,
  onCancel,
}: WorkbenchEntryComposerProps) {
  const legKind = defaultLeg
  const leg = legByKind(legKind)
  // Position/trade mode passes its own lead/label/tone; otherwise fall back to the leg's.
  const dLabel = label ?? leg.label
  const dLead = lead ?? leg.lead
  const dTone = tone ?? leg.tone
  const dGlyph = glyph ?? leg.glyph
  const dPh = placeholder ?? leg.ph
  const [value, setValue] = useState(initialValue)
  const taRef = useRef<HTMLTextAreaElement | null>(null)
  const cardRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // preventScroll: the card is already clamped inside the chart pane, and
    // letting focus scroll ancestors shifts the whole workbench layout
    // horizontally (drastically so at mobile widths).
    taRef.current?.focus({ preventScroll: true })
  }, [])

  // Card shrinks below MAX_W on panes too narrow to hold it (phones), so the
  // clamp below can always keep it fully inside the chart.
  const w = Math.max(MIN_W, Math.min(MAX_W, paneW - 16))

  // Open above the click, clamped inside the chart so it never spills off an edge. Measure the
  // rendered card, then write left/top straight onto the element (no setState in the effect).
  // `value` is a deliberate dep: the textarea grows with the text, changing the card height.
  useLayoutEffect(() => {
    const card = cardRef.current
    if (!card) return
    const h = card.offsetHeight
    let left = x + 12
    let top = y - h - 12
    if (top < 8) top = y + 16
    left = Math.max(8, Math.min(left, paneW - w - 8))
    top = Math.max(8, Math.min(top, paneH - h - 8))
    card.style.left = `${left}px`
    card.style.top = `${top}px`
  }, [x, y, paneW, paneH, w, value])

  const submit = () => {
    // The comment is optional — save whatever's there, including an empty string.
    onSubmit(legKind, value.trim())
  }
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      onCancel()
    }
  }

  return (
    <div
      ref={cardRef}
      className="absolute z-[120] animate-composer-in"
      style={{ left: x + 12, top: y + 12, width: w }}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="ai-grad-border">
        <div className="ai-capture-surface p-3.5">
          <div className="flex items-center gap-2 mb-2">
            {dGlyph && <span className={toneClass(dTone)}>{dGlyph}</span>}
            <span className="text-label text-foreground">{dLabel}</span>
            <span className="text-caption text-muted-foreground">
              {price} · {timeLabel}
            </span>
            <button
              onClick={onCancel}
              className="ml-auto text-muted-foreground hover:text-foreground"
              aria-label="Cancel"
            >
              <X size={14} />
            </button>
          </div>
          <div className="text-body text-muted-foreground mb-1.5">
            {dLead}&hellip; <span className="text-muted-foreground/60">(optional)</span>
          </div>
          <textarea
            ref={taRef}
            rows={3}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={dPh}
            className="w-full resize-none bg-transparent text-body text-foreground placeholder:text-muted-foreground/60 outline-none"
          />
          <div className="flex items-center justify-end gap-1">
            <button
              onClick={onCancel}
              className="text-label text-muted-foreground hover:text-foreground px-2 py-1.5"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              className="ai-cta flex items-center gap-1 rounded-md px-2.5 py-1.5 text-label"
            >
              Save
              <ArrowRight size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
